<template>
  <v-menu
    offset-y
    transition="scale-transition"
    v-model="menu"
    :disabled="_readonly"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        ref="field"
        hide-details="auto"
        outlined
        dense
        v-model="search"
        :items="_items"
        v-on="{ ...on, ...$listeners }"
        v-bind="{ ...attrs, ...$attrs }"
        @input="v => isOnlySearch && fetchItems(v)"
        @blur="onBlur"
        :readonly="_readonly"
      >
        <slot v-for="slot in Object.keys($slots)" :name="slot" :slot="slot" />
      </v-text-field>
      <BasePopup
        v-if="canDirect"
        width="450px"
        v-model="popup"
        title="직접입력"
        :confirmCallback="onDirect"
      >
        <v-form ref="directForm" lazy-validation>
          <div>
            <v-text-field
              v-if="popup"
              hide-details
              :label="directLabel"
              dense
              outlined
              :rules="[required]"
              autofocus
              v-model="direct.name"
            />
            <template v-if="itemKey == 'school'">
              <v-text-field
                class="mt-4"
                hide-details
                label="소재지"
                dense
                :rules="collectionType !== 'high' ? [required] : []"
                outlined
                v-model="direct.region"
              />
              <v-text-field
                class="mt-4"
                hide-details
                :rules="collectionType !== 'high' ? [required] : []"
                label="캠퍼스"
                dense
                outlined
                v-model="direct.campusName"
              />
            </template>
          </div>
        </v-form>
      </BasePopup>
    </template>
    <v-list
      style="max-height:300px;overflow-y:auto;font-size:0.8rem;"
      class="pt-0"
    >
      <v-progress-linear
        :active="loading"
        :indeterminate="loading"
      ></v-progress-linear>
      <v-list-item v-if="canDirect && !loading" class="cursor mt-2">
        <span
          class="py-1 mx-auto direct-btn"
          style="width:100%;border-radius:14px"
          @click.stop="openPopup"
        >
          직접입력
        </span>
      </v-list-item>
      <v-list-item v-if="!_items.length">
        {{
          loading
            ? '검색중'
            : search
            ? '검색결과가 없습니다.'
            : '검색어를 입력하세요'
        }}
      </v-list-item>
      <!-- onClick 사용하게되면 blur 가 먼저 event를 발생하게 됨  -->
      <v-list-item
        v-else
        v-for="(item, i) in _items"
        :key="i"
        class="auto-list-item cursor"
        @click="onInput(item, i)"
        @mousedown="onInput(item, i)"
      >
        {{ item }}
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { API } from '@/api/network';
import { debounce } from '@/utils/util';
import BasePopup from './BasePopup.vue';

let searchIndex = 0;

const url = {
  language: '/cv/languages',
  license: '/cv/licenses',
  school: '/cv/schools',
  major: '/cv/school/majors',
  class: '/cv/school/majors/class',
};

export default {
  components: { BasePopup },
  props: {
    directInput: {
      type: [String, Boolean],
      default: false,
    },
    itemKey: {
      type: String,
      required: true,
    },
    collectionType: {
      type: String,
      default: 'normal',
    },
    value: {
      type: String,
    },
    readonly: {
      type: Boolean,
    },
    campusShow: {
      type: Boolean,

      default: false,
    },
    debounceDelay: {
      type: Number,
      default: 250,
    },
  },
  data() {
    return {
      popup: false,
      direct: {
        name: '',
        region: '',
        campusName: '',
      },
      menu: false,
      items: [],
      loading: false,
      selected: '',
    };
  },
  watch: {
    search(v) {
      if (v && v != this.selected) {
        this.fetchItems(v);
        this.menu = true;
      }
    },
  },
  inject: {
    form: {
      default: null,
    },
  },
  mounted() {
    this.selected = this.search;
    this.form?.register?.(this.$refs.field);
  },
  computed: {
    directLabel() {
      return this.$attrs.label?.replace('*', '');
    },
    nameKey() {
      return this.itemKey === 'school'
        ? 'schoolName'
        : this.itemKey === 'class'
        ? 'class'
        : 'name';
    },
    _readonly() {
      return this.readonly ?? this.form?.readonly ?? false;
    },
    search: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    canDirect() {
      return this.directInput === 'Y' || this.directInput === true;
    },
    _items() {
      return this.items.map(v => this.parser(v)).filter(v => v);
    },
    isOnlySearch() {
      // v-model 없이 검색 용도 인 경우
      return !this.$listeners.input;
    },
  },
  methods: {
    async onBlur() {
      await this.$nextTick();
      if (this.selected != this.value) {
        this.$emit('input', !this.value ? '' : this.selected);
      }
      this.items = [];
    },
    parser(item) {
      if (this.itemKey == 'school' && this.campusShow) {
        return item.campusName
          ? `${item.schoolName} [${item.campusName}]`
          : item.schoolName;
      }
      return item[this.nameKey];
    },
    onInput(keyword, index) {
      this.selected = keyword;
      this.search = keyword;
      if (this.isOnlySearch) this.$refs.field.reset();
      this.$emit('change:item', this.items[index]);
      this.$emit('change:keyword', keyword);
    },
    fetchItems(searchWord) {
      const index = ++searchIndex;
      if (!url[this.itemKey] || !searchWord || this.readonly) return;
      this.loading = true;
      debounce(
        async () => {
          try {
            const { data } = await API.postAuthJson(url[this.itemKey], {
              searchWord,
              type: this.collectionType,
              rows: 20,
            });
            // 검색된 items 가 많을수록 오래 걸리기때문에 순서 보장을 위한 index
            if (index == searchIndex) this.items = data.data || [];
          } catch (error) {
            this.items = [];
            console.error(error);
          } finally {
            this.loading = false;
          }
        },
        `autoComplete${this.itemKey}`,
        this.debounceDelay,
      );
    },
    openPopup() {
      Object.assign(this, {
        menu: false,
        direct: {
          name: '',
          region: '',
          campusName: '',
        },
      });
      this.$refs.directForm?.reset();
      this.popup = true;
    },
    onDirect(closePopup) {
      if (!this.$refs.directForm.validate()) return;
      closePopup();
      const name =
        this.direct.name +
        (this.itemKey == 'school' && this.direct.campusName
          ? `[${this.direct.campusName}]`
          : '');
      this.selected = name;
      this.search = name;
      const item = {
        ...this.direct,
        [this.nameKey]: this.direct.name,
        directYn: 'Y',
        itemKey: this.itemKey,
      };
      this.$emit('change:item', item);
      this.$emit('change:keyword', this.direct.name);
    },
    required(v) {
      return !!v.replaceAll(' ', '') || '필수 입력 항목';
    },
  },
};
</script>

<style lang="scss" scoped>
.auto-list-item {
  &:hover {
    background-color: rgba($color: #363c43, $alpha: 0.1);
  }
}
::v-deep {
  .v-list .v-list-item {
    min-height: 34px !important;
  }
}
.direct-btn {
  transition: background-color 0.1s;
  background-color: #eee;
  text-align: center;
  &:hover {
    background-color: rgba($color: #363c43, $alpha: 0.2);
  }
}
</style>
